import React, { useContext } from 'react';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { FormFieldRow } from '../../../../shared/form/FormFieldRow';
import { SearchSelectField } from '../../../../shared/form/inputs/SearchSelectField';
import { SelectOptions } from '../../../../shared/form/inputs/Select';
import { SelectField } from '../../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../../shared/form/inputs/TextInputField';
import { ManifoldFloorConstructionFormModel } from '../GetDataForQuoteOrDesignForm';
import { isQuoteFormContextValue, QuoteOrDesignFormContext } from '../QuoteOrDesignFormContext';
import {
  floorLevelSelectOptions,
  getQuoteRoomlessManifoldThermostatSelectOptions,
  nonSharedThermostatSelectOptions,
} from '../quoteOrDesignFormData';
import { manifoldFieldName } from '../quoteOrDesignFormFieldNameHelpers';
import { RoomlessManifoldFormModel } from './manifoldFormModel';

type Props = {
  roomlessManifold: RoomlessManifoldFormModel;
  unitIndex: number;
  manifoldIndex: number;
  floorConstructions: SelectOptions<ManifoldFloorConstructionFormModel>;
};

export const RoomlessManifoldForm = ({ roomlessManifold, unitIndex, manifoldIndex, floorConstructions }: Props) => {
  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const isQuoteForm = isQuoteFormContextValue(formContext);

  const { setFieldValue } = formContext.formProps;

  const roomlessManifoldFieldName = (fieldName: string) =>
    manifoldFieldName(unitIndex, manifoldIndex, `roomlessManifold.${fieldName}`);

  const handleFloorConstructionChange = (selectedItem: ManifoldFloorConstructionFormModel | null) => {
    if (selectedItem?.isHighOutput && roomlessManifold.thermostatTypeCode !== 'Shared') {
      setFieldValue(roomlessManifoldFieldName('numberOfFloorSensors'), '1');
    }

    if (selectedItem?.isElectricUnderfloorHeating) {
      setFieldValue(roomlessManifoldFieldName('numberOfPorts'), '');
    }
  };

  return (
    <>
      <FormFieldRow>
        <SelectField
          name={roomlessManifoldFieldName('floorLevelCode')}
          label="Level"
          options={floorLevelSelectOptions}
        />
        <SearchSelectField
          name={roomlessManifoldFieldName('floorConstruction')}
          label="Construction"
          mapValueToKey={value => `${value.partId}`}
          options={floorConstructions}
          onChange={handleFloorConstructionChange}
        />
        <TextInputField
          name={roomlessManifoldFieldName('areaInMetresSquared')}
          label={
            <>
              Area (m<sup>2</sup>)
            </>
          }
        />
        {roomlessManifold.floorConstruction != null &&
        roomlessManifold.floorConstruction.isElectricUnderfloorHeating ? null : (
          <TextInputField name={roomlessManifoldFieldName('numberOfPorts')} label="Ports" />
        )}
        <SelectField
          name={roomlessManifoldFieldName('thermostatTypeCode')}
          label="Thermostat"
          options={
            isQuoteForm
              ? getQuoteRoomlessManifoldThermostatSelectOptions(
                  formContext.formProps.values.thermostatTypeCode,
                  roomlessManifold.thermostatTypeCode,
                )
              : nonSharedThermostatSelectOptions
          }
        />
        <TextInputField name={roomlessManifoldFieldName('numberOfThermostats')} label="Thermostats" />
        <TextInputField name={roomlessManifoldFieldName('numberOfWetSensors')} label="Wet Sensors" />
        <TextInputField name={roomlessManifoldFieldName('numberOfFloorSensors')} label="Dry Sensors" />
      </FormFieldRow>
    </>
  );
};
